<script setup
        lang="ts"
>
import { getLocaleValue } from '@/js/helper/locale.ts';
import BaseIcon from '@/js/vue/base/base-icon.vue';
import BaseInput from '@/js/vue/base/base-input.vue';
import { ref, watch } from 'vue';

const props = defineProps<{
  name: string
  max: number
  min: number
  openByDefault?: boolean
}>();

const range = defineModel<{ from: number, to: number } | null | undefined>('range', {
  required: true,
});

const innerRange = ref({
  from: null,
  to: null,
} as {
  from: number | null
  to: number | null
});

watch(innerRange, (newRange) => {
  let { from, to } = newRange;
  if (from === '') {
    from = null;
  }
  if (to === '') {
    to = null;
  }
  const v = {
    from: from ?? props.min,
    to: to ?? props.max,
  };

  if (v.from === props.min && v.to === props.max) {
    range.value = null;
  }
  else {
    range.value = v;
  }
}, {
  immediate: true,
  deep: true,
});

watch(range, (newRange, oldRange) => {
  if (newRange?.from === oldRange?.from && newRange?.to === oldRange?.to) {
    return;
  }

  if (!newRange) {
    innerRange.value = {
      from: null,
      to: null,
    };
    return;
  }

  innerRange.value = {
    from: newRange.from === props.min ? null : newRange.from,
    to: newRange.to === props.max ? null : newRange.to,
  };
}, {
  immediate: true,
});

// Count only once
const openByDefault = Boolean(range.value) || props.openByDefault;
</script>

<template>
  <details class="filters__details" :open="openByDefault">
    <summary class="filters__summary">
      <span v-html="name" />
      <BaseIcon
        type="main"
        icon="chevron"
        :width="16"
        :height="16"
        class="filters__summary-icon"
      />
    </summary>
    <div class="filters__details-content">
      <div class="filters__range-options js-floating-container">
        <div class="base-field filters__range-option">
          <div class="filters__range-option-label">
            {{ getLocaleValue('from') }}:
          </div>
          <BaseInput
            v-model.number="innerRange.from"
            type="number"
            :placeholder="min"
          />
        </div>
        <div class="base-field filters__range-option">
          <div class="filters__range-option-label">
            {{ getLocaleValue('to') }}:
          </div>
          <BaseInput
            v-model.number="innerRange.to"
            type="number"
            :placeholder="max"
          />
        </div>
      </div>
    </div>
  </details>
</template>
